import { Fragment } from 'react';
import { ArrowUpRight } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Card, CardHeader, StatTile } from '../../components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { InfoTip } from '../../components/ui/InfoTip';
import { Badge } from '../../components/ui/Badge';
import { Table, THead, Th, Td, Tr, Nil, TableFooter, TGroupRow, TTotalRow } from '../../components/ui/Table';
import { useWebAnalytics } from '../../hooks/useWebAnalytics';
import { useIdentifiedPeople, useJourneys, useVisitingCompanies } from '../../hooks/useJourneys';
import { ATTIO_COMPANIES_URL, CompaniesCard } from './CompaniesCard';
import { LinkedInLeadsCard } from './LinkedInLeadsCard';
import { PeopleCard } from './PeopleCard';
import type {
  WebAnalyticsData,
  WebAnalyticsChannelRow,
  WebAnalyticsPageRow,
  WebAnalyticsHealth,
  WebAnalyticsConsent,
  ListPeriod,
} from '../../api/web-analytics';
import { fmtNumber, fmtDelta } from '../Dashboard/gtm/components/format';
import { knownPeople } from './known-people';
import { CHART_AXIS, CHART_GRID, CHART_HEIGHT, CHART_LEGEND, CHART_SERIES, CHART_TOOLTIP } from '../../components/ui/chart';
import { formatDayMonth, formatDayMonthTime, formatDayMonthYear } from '../../utils/dateFormat';
import {
  Toolbar,
  UnavailableSources,
  channelLabel,
  humanize,
  isTestSource,
  MarketingPeriodControl,
  useMarketingPeriod,
} from './shared';
import { foldClickIdRows, groupChannelRows } from './channel-groups';

/** ISO timestamp → "3h ago" / "2d ago" / "just now". */
function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Adapt fmtDelta's { text, trend } to StatTile's { value, trend } change prop. */
function deltaChange(deltaPercent: number | null): { value: string; trend: 'up' | 'down' | 'neutral' } {
  const d = fmtDelta(deltaPercent);
  return { value: d.text, trend: d.trend };
}

/**
 * Marketing → Website: one page for the website, top to bottom — how much
 * traffic, which companies, which people. Traffic is our own first-party
 * tracking; companies and named people come from RB2B and from forms.
 */
export function WebsiteView() {
  const p = useMarketingPeriod();
  const { period, label } = p;
  const { data, isLoading, error, refetch } = useWebAnalytics(period.startDate, period.endDate);

  return (
    <div className="space-y-6">
      <Toolbar
        start={data ? <HealthLine health={data.health} /> : null}
        end={<MarketingPeriodControl p={p} />}
      />

      {isLoading ? (
        <LoadingState rows={4} />
      ) : error || !data ? (
        <ErrorState
          title="Couldn’t load website analytics"
          error={error}
          details="Check the API and the website tracker (/api/collect)."
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {data.health?.stale && <StalePipeline health={data.health} />}
          <UnavailableSources sources={data.unavailable} label={humanize} />

          <Overview data={data} period={period} label={label} />
          <CookieConsent consent={data.consent ?? null} label={label} />
          <IntentNotInAttio period={period} label={label} />

          <div className="flex items-center gap-2">
            <h3 className="text-heading-sm text-text-primary">Traffic</h3>
            <InfoTip>
              Our own website tracking (RudderStack, then /api/collect from 2 Oct 2026). It counts only visitors who
              accept cookies, and leaves out our own team and bots. GA4 counts sessions its own way, so the two
              never match exactly: the Sessions tile shows both.
            </InfoTip>
          </div>
          <TrafficTrend data={data} label={label} />
          {/* Stacked, not side by side: the grouped channel table and the page list are
              never the same height, and a pair left a large empty area beside the shorter one. */}
          <ChannelBreakdown rows={data.channels} totalSessions={data.overview?.sessions ?? null} label={label} />
          <TopPages rows={data.topPages} label={label} />
          <CompaniesCard period={period} label={label} />
          <PeopleCard period={period} label={label} />
          <LinkedInLeadsCard period={period} label={label} />
        </>
      )}
    </div>
  );
}

/** Quiet liveness line; the loud version only renders when the pipeline is stale. */
function HealthLine({ health }: { health: WebAnalyticsHealth | null }) {
  if (!health || health.stale) return null;
  return (
    <p className="text-body-xs text-text-tertiary">
      Last event {health.lastEventAt ? timeAgo(health.lastEventAt) : 'never'} · {fmtNumber(health.eventsLast24h)} events
      in the last 24h
    </p>
  );
}

/** Pipeline liveness — loud when stale, never a silent breakage. */
function StalePipeline({ health }: { health: WebAnalyticsHealth }) {
  return (
    <ErrorState
      title="Website event pipeline looks broken"
      error={
        health.lastEventAt
          ? `No events received since ${formatDayMonthTime(health.lastEventAt)} (${timeAgo(health.lastEventAt)}): threshold is ${health.staleThresholdHours}h.`
          : 'No events have ever been received.'
      }
      details="Check the website tracker and /api/collect in the API logs."
    />
  );
}

/** "OS 1,126 vs GA4 1,191 (+65, GA4 6% higher)": the two session counts side by side. */
function sessionsBridge(os: number, ga4: number | null | undefined): string {
  if (ga4 === undefined || ga4 === null) return 'GA4 sessions unavailable for comparison';
  const diff = ga4 - os;
  if (diff === 0) return `GA4 counts the same ${fmtNumber(ga4)}`;
  const pct = os > 0 ? Math.round((Math.abs(diff) / os) * 100) : null;
  return `GA4 counts ${fmtNumber(ga4)} (${diff > 0 ? '+' : '−'}${fmtNumber(Math.abs(diff))}${pct !== null ? `, ${pct}%` : ''}), as on Overview`;
}

function Overview({ data, period, label }: { data: WebAnalyticsData; period: ListPeriod; label: string }) {
  const companies = useVisitingCompanies(period);
  const journeys = useJourneys(period);
  const identified = useIdentifiedPeople(period);
  const o = data.overview;
  // The lists stop at 100 rows; the API sends the real totals (older builds don't: then "100+").
  const listedCompanies = companies.data?.companies.length;
  const companiesTotal = companies.data?.identifiedTotal ?? null;
  const companiesCut = listedCompanies !== undefined && (companiesTotal !== null ? companiesTotal > listedCompanies : listedCompanies >= 100);
  const rb2bListed = identified.data?.people.length ?? 0;
  const rb2bTotal = identified.data?.total ?? null;
  const peopleCut = identified.data !== undefined && (rb2bTotal !== null ? rb2bTotal > rb2bListed : rb2bListed >= 100);
  // The same set as the "People known by name" table, by the same rule.
  const named =
    journeys.data && identified.data
      ? (() => {
          const k = knownPeople(journeys.data.journeys, identified.data.people);
          return k.forms.length + k.rb2b.length;
        })()
      : undefined;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatTile
        label="Visitors · OS tracking"
        value={o ? fmtNumber(o.visitors) : '—'}
        change={o ? deltaChange(o.visitorsDelta) : undefined}
        sub={`${label} · first-party, cookie-consented only`}
        tooltip="Our own website tracking (/api/collect). Not GA4, and not Semrush's traffic estimate."
      />
      <StatTile
        label="Sessions · OS tracking"
        value={o ? fmtNumber(o.sessions) : '—'}
        change={o ? deltaChange(o.sessionsDelta) : undefined}
        sub={`${label} · first-party · ${o ? sessionsBridge(o.sessions, data.ga4Sessions) : 'GA4 unavailable'}`}
        tooltip="OS tracking starts a new session after 30 minutes without activity and counts only cookie-consented visitors, without our team or bots. GA4 also restarts sessions at midnight and on a new campaign, and models visitors who decline cookies. Marketing → Overview's funnel uses the GA4 figure."
      />
      <StatTile
        label="Companies identified"
        value={listedCompanies === undefined ? '—' : companiesTotal !== null ? fmtNumber(companiesTotal) : companiesCut ? '100+' : fmtNumber(listedCompanies)}
        sub={`${label} · RB2B${companiesCut ? ` · the ${fmtNumber(listedCompanies ?? 0)} with most visits are listed below` : ''}`}
      />
      <StatTile
        label="People known by name"
        value={named === undefined ? '—' : peopleCut ? `${fmtNumber(named)}+` : fmtNumber(named)}
        sub={`${label} · forms + RB2B${peopleCut ? `, the ${fmtNumber(rb2bListed)} most recent of ${rb2bTotal !== null ? fmtNumber(rb2bTotal) : 'more'} RB2B-named are listed` : ''}`}
      />
    </div>
  );
}

/**
 * The exception to act on: companies with 50+ staff RB2B saw on a pricing or
 * demo page that aren't in Attio yet (so they have no ICP category; schools and
 * universities are left out). Nothing renders when there are none.
 */
function IntentNotInAttio({ period, label }: { period: ListPeriod; label: string }) {
  const { data, error } = useVisitingCompanies(period);
  if (error) {
    return <ErrorState compact title="Couldn’t check pricing and demo visits against Attio" error={error} />;
  }
  const rows = (data?.companies ?? []).filter((c) => c.intentPages.length > 0 && c.icpFit && !c.attioUrl);
  if (rows.length === 0) return null;
  return (
    <Card padding="sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="warning">
          {rows.length} {rows.length === 1 ? 'company' : 'companies'} with 50+ staff (not in Attio) on pricing or demo
        </Badge>
        <span className="text-body-xs text-text-tertiary">
          {label} · RB2B · not in Attio, so no ICP category: size by RB2B’s band, schools and universities left out
        </span>
      </div>
      <ul className="mt-3 divide-y divide-border-light">
        {rows.map((c) => (
          <li key={c.domain} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
            <div className="min-w-0">
              <a
                href={`https://${c.domain}`}
                target="_blank"
                rel="noreferrer"
                className="text-body-sm text-text-primary hover:underline"
              >
                {c.name}
              </a>
              <p className="text-body-xs text-text-tertiary break-words">
                {[c.domain, c.employeeCount ? `${c.employeeCount} employees` : null, c.intentPages.join(', '),
                  c.lastVisitAt ? `last visit ${formatDayMonth(c.lastVisitAt)}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <a
              href={ATTIO_COMPANIES_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                // Attio has no search-by-URL link: the domain goes on the clipboard to paste into its search.
                void navigator.clipboard?.writeText(c.domain).catch(() => undefined);
              }}
              title={`Opens Attio companies with “${c.domain}” copied, to search for it or add it`}
              className="inline-flex items-center gap-1 text-body-xs text-accent hover:underline"
            >
              Add in Attio <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

/**
 * What share of visitors accept cookies: the tracker's visitors (accepting
 * only) against every real browser in the CloudFront logs, day by day. The
 * CloudFront count starts on 16 Oct 2026, so earlier days are left out of the
 * rate and the card says how many days it covers.
 */
function CookieConsent({ consent, label }: { consent: WebAnalyticsConsent | null; label: string }) {
  if (!consent) return null;
  const partial = consent.coveredDays < consent.periodDays;
  return (
    <Card>
      <CardHeader
        title="Cookie consent"
        description={`${label} · tracked visitors ÷ every real browser (CloudFront logs)${
          partial ? ` · ${consent.coveredDays} of ${consent.periodDays} days have a browser count` : ''
        }`}
      />
      {consent.coveredDays === 0 ? (
        <EmptyState
          compact
          title="No browser counts for this period"
          description="Counted daily from the CloudFront logs since 16 Oct 2026."
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              label="Accept rate"
              value={pct(consent.consentedVisitors, consent.allBrowsers)}
              sub={`${fmtNumber(consent.consentedVisitors)} of ${fmtNumber(consent.allBrowsers)} browsers`}
              tooltip="Visitors the OS tracker saw (they accepted statistics or marketing cookies) divided by every real browser that loaded the site, both counted per day and summed. Team traffic is in both."
            />
            <StatTile label="All browsers" value={fmtNumber(consent.allBrowsers)} sub="whatever their cookie choice" />
            <StatTile
              label="One page only"
              value={pct(consent.singlePageBrowsers, consent.allBrowsers)}
              sub={`${fmtNumber(consent.singlePageBrowsers)} browsers left after one page`}
            />
            <StatTile
              label="On mobile"
              value={pct(consent.mobileBrowsers, consent.allBrowsers)}
              sub={`${fmtNumber(consent.mobileBrowsers)} browsers`}
            />
          </div>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT.tall}>
            <LineChart data={consent.daily} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="date" tickFormatter={(d) => formatDayMonth(String(d))} {...CHART_AXIS} minTickGap={32} />
              <YAxis {...CHART_AXIS} width={44} />
              <Tooltip labelFormatter={(d) => formatDayMonthYear(String(d))} {...CHART_TOOLTIP} />
              <Legend {...CHART_LEGEND} />
              <Line type="linear" dataKey="browsers" name="All browsers" stroke={CHART_SERIES[2]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
              <Line type="linear" dataKey="consented" name="Accepted cookies" stroke={CHART_SERIES[0]} dot={false} strokeWidth={2} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
          <Table colWidths={['auto', '6rem', '6rem', '5rem']}>
            <THead>
              <tr>
                <Th>Channel (landing page)</Th>
                <Th align="right">Browsers</Th>
                <Th align="right">Accepted</Th>
                <Th align="right">Rate</Th>
              </tr>
            </THead>
            <tbody>
              {consent.byChannel.map((r) => (
                <Tr key={r.channel}>
                  <Td className="text-text-secondary">{channelLabel(r.channel)}</Td>
                  <Td align="right" className="tabular-nums">{fmtNumber(r.browsers)}</Td>
                  <Td align="right" className="tabular-nums">{fmtNumber(r.consented)}</Td>
                  <Td align="right" className="tabular-nums">{pct(r.consented, r.browsers)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <TableFooter>
            A browser is an IP address + user agent that ran the page; Google, cloud servers and crawlers are left out.
            A phone that changes network counts twice, so the true rate is a little higher.
          </TableFooter>
        </div>
      )}
    </Card>
  );
}

function TrafficTrend({ data, label }: { data: WebAnalyticsData; label: string }) {
  const points = data.trend ?? [];
  return (
    <Card>
      <CardHeader title="Daily visitors and sessions" description={`${label} · OS tracking`} />
      {points.length === 0 ? (
        <EmptyState compact title="No traffic in this period" />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT.tall}>
          <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid {...CHART_GRID} />
            <XAxis dataKey="date" tickFormatter={(d) => formatDayMonth(String(d))} {...CHART_AXIS} minTickGap={32} />
            <YAxis {...CHART_AXIS} width={44} />
            <Tooltip labelFormatter={(d) => formatDayMonthYear(String(d))} {...CHART_TOOLTIP} />
            <Legend {...CHART_LEGEND} />
            <Line type="linear" dataKey="visitors" name="Visitors" stroke={CHART_SERIES[0]} dot={false} strokeWidth={2} isAnimationActive={false} />
            <Line type="linear" dataKey="sessions" name="Sessions" stroke={CHART_SERIES[2]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

/** Source rows listed per channel on "Where sessions come from"; smaller ones are summed under it. */
const ROWS_PER_CHANNEL = 5;

/** "(direct) / (none)" and other GA-style placeholders say nothing beyond the channel. */
const isPlaceholder = (v: string) => v === '' || v === '(direct)' || v === '(none)' || v === '(not set)';

function ChannelBreakdown({
  rows: allRows,
  totalSessions,
  label,
}: {
  label: string;
  rows: WebAnalyticsChannelRow[] | null;
  /** The Sessions tile's figure: the table reconciles to it. */
  totalSessions: number | null;
}) {
  const rows = allRows?.filter((r) => !isTestSource(r.source, r.medium)) ?? null;
  const tests = allRows?.filter((r) => isTestSource(r.source, r.medium)) ?? [];
  const testSessions = tests.reduce((n, r) => n + r.sessions, 0);
  // GTM Tag Assistant and other debug loads: not visitors, so not a channel.
  const internal = rows?.filter((r) => r.channel === 'internal') ?? [];
  const internalSessions = internal.reduce((n, r) => n + r.sessions, 0);
  // Every source row is grouped (not a top-N cut), so each channel's total is the one Overview shows.
  const groups = groupChannelRows(
    foldClickIdRows((rows ?? []).filter((r) => r.channel !== 'internal')),
    (r) => channelLabel(r.channel, r.source, r.medium),
    ROWS_PER_CHANNEL,
  );
  const listed = groups.reduce((n, g) => n + g.sessions, 0);
  // The API returns up to 300 source rows; anything beyond is real but unlisted,
  // shown as its own row so the table adds up to the tile.
  const other =
    totalSessions === null ? 0 : Math.max(0, totalSessions - listed - testSessions - internalSessions);
  const tableTotal = listed + other;
  return (
    <Card>
      <CardHeader title="Where sessions come from" description={`${label} · first session event’s source · OS tracking`} />
      {groups.length === 0 ? (
        <EmptyState compact title="No channel data in this period" />
      ) : (
        <Table colWidths={['auto', '6rem', '6rem']}>
          <THead>
            <tr>
              <Th>Channel · source / medium</Th>
              <Th align="right">Sessions</Th>
              <Th align="right">Visitors</Th>
            </tr>
          </THead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.label}>
                <TGroupRow
                  colSpan={3}
                  label={g.label}
                  summary={`${fmtNumber(g.sessions)} ${g.sessions === 1 ? 'session' : 'sessions'}`}
                />
                {g.rows.map((r) => (
                  <Tr key={`${r.channel}/${r.source}/${r.medium}`}>
                    <Td className="pl-6 text-text-secondary break-words">
                      {isPlaceholder(r.source) && isPlaceholder(r.medium) ? (
                        <span className="text-text-tertiary">No source</span>
                      ) : (
                        <>
                          {isPlaceholder(r.source) ? <Nil /> : r.source}
                          {/* "/ referral" only repeats that the source is a referring site; the group names the channel. */}
                          {!isPlaceholder(r.medium) && r.medium !== 'referral' && (
                            <span className="text-text-tertiary"> / {r.medium}</span>
                          )}
                        </>
                      )}
                    </Td>
                    <Td align="right" className="tabular-nums">{fmtNumber(r.sessions)}</Td>
                    <Td align="right" className="tabular-nums">{fmtNumber(r.visitors)}</Td>
                  </Tr>
                ))}
                {g.rest.count > 0 && (
                  <Tr>
                    <Td className="pl-6 text-text-tertiary">
                      {fmtNumber(g.rest.count)} smaller {g.rest.count === 1 ? 'source' : 'sources'}
                    </Td>
                    <Td align="right" className="tabular-nums">{fmtNumber(g.rest.sessions)}</Td>
                    <Td align="right" title="Visitors can't be summed across sources (one visitor can use several)"><Nil /></Td>
                  </Tr>
                )}
              </Fragment>
            ))}
            {other > 0 && (
              <Tr>
                <Td className="text-text-secondary" title="Sources beyond the rows the API returns; their sessions are counted, not itemised">
                  Other / unclassified
                  <span className="text-text-tertiary"> · sources beyond the listed 300</span>
                </Td>
                <Td align="right" className="tabular-nums">{fmtNumber(other)}</Td>
                <Td align="right" title="Visitors can't be summed across sources (one visitor can use several)"><Nil /></Td>
              </Tr>
            )}
          </tbody>
          <tfoot>
            <TTotalRow>
              <Td>Total</Td>
              <Td align="right" className="tabular-nums">{fmtNumber(tableTotal)}</Td>
              <Td align="right" title="Visitors can't be summed across sources (one visitor can use several)"><Nil /></Td>
            </TTotalRow>
          </tfoot>
        </Table>
      )}
      {groups.length > 0 && (
        <TableFooter>
          {[
            totalSessions === null
              ? 'Session total unavailable, so smaller sources are not reconciled'
              : `${fmtNumber(tableTotal)}${testSessions > 0 ? ` + ${fmtNumber(testSessions)} test` : ''}${
                  internalSessions > 0 ? ` + ${fmtNumber(internalSessions)} internal/debug (GTM Tag Assistant)` : ''
                } = ${fmtNumber(tableTotal + testSessions + internalSessions)} sessions${
                  tableTotal + testSessions + internalSessions === totalSessions ? ', the Sessions tile' : `; the tile says ${fmtNumber(totalSessions)}`
                }`,
            'channel totals match Overview’s clicks → tracked sessions',
            'an untagged ad click (gclid, msclkid, li_fat_id) counts under its platform’s cpc row',
          ]
            .filter(Boolean)
            .join(' · ')}
        </TableFooter>
      )}
    </Card>
  );
}

function TopPages({ rows, label }: { rows: WebAnalyticsPageRow[] | null; label: string }) {
  return (
    <Card>
      <CardHeader title="Top pages" description={`${label} · page views · OS tracking`} />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No page views in this period" />
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Page</Th>
              <Th align="right">Views</Th>
              <Th align="right">Visitors</Th>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.page}>
                <Td className="text-text-primary break-all">
                  {r.page.replace(/^https?:\/\/[^/]+/, '') || '/'}
                </Td>
                <Td align="right" className="tabular-nums">{fmtNumber(r.pageviews)}</Td>
                <Td align="right" className="tabular-nums">{fmtNumber(r.visitors)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
